import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared hook that wires up a hidden <input type="file"> to the backend
 * /api/auth/me/photo endpoint so any role (guest, host, staff, admin)
 * can upload a profile picture. Persists the updated user object to
 * localStorage so chat avatars and other surfaces pick it up live.
 *
 * Returns: { fileInputRef, uploading, openPicker, handleFileChange, user }
 */
export function useProfilePhotoUpload(initialUser) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(
    () => initialUser || JSON.parse(localStorage.getItem('user') || '{}')
  );

  const openPicker = useCallback(() => {
    if (uploading) return;
    fileInputRef.current?.click();
  }, [uploading]);

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/auth/me/photo`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUser = res.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      // Notify nav avatars / other surfaces in this tab to refresh live.
      window.dispatchEvent(new Event('allure:profile-photo-updated'));
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  return { fileInputRef, uploading, openPicker, handleFileChange, user, setUser };
}

export default useProfilePhotoUpload;
